// src/components/kuestion/campus/Dashboard.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../../../config/apiConfig';
import { useAuth } from '../../../context/AuthContext';
import { useCampus } from './CampusContext';
import { Stat, RuleHead } from '../shared/Bits';
import { PlusIcon, UploadIcon, FileIcon, DownloadIcon } from '../shared/Icons';
import { fmtLongDay, greeting, isOpenNow, isProctored, parseDate, relativeWindow } from '../shared/format';
import { kq } from '../../../config/routes';

const DAY = 24 * 3600 * 1000;

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { api, students, assignments, reports } = useCampus();
  const [cohort, setCohort] = useState(null);

  useEffect(() => {
    let live = true;
    api.get(API_ENDPOINTS.CAMPUS_ANALYTICS_COHORT)
      .then((d) => { if (live) setCohort(d); })
      .catch(() => { if (live) setCohort({ modules: [] }); });
    return () => { live = false; };
  }, [api]);

  const stats = useMemo(() => {
    const active = students.filter((s) => s.is_active !== false).length;
    const open = assignments.filter((a) => isOpenNow(a.opens_at, a.closes_at));
    const closingSoon = open
      .filter((a) => a.closes_at && parseDate(a.closes_at).getTime() - Date.now() < DAY)
      .sort((a, b) => parseDate(a.closes_at) - parseDate(b.closes_at));
    const awaiting = open.filter((a) => isProctored(a.mode))
      .reduce((n, a) => n + Math.max(0, (a.student_count || 0) - (a.completed_count || 0)), 0);
    const awaitingFrom = open.find((a) => isProctored(a.mode) && (a.student_count || 0) > (a.completed_count || 0));
    const mods = (cohort?.modules || []).filter((m) => m.sufficient !== false && m.strength != null);
    const avg = mods.length ? Math.round(mods.reduce((n, m) => n + Number(m.strength), 0) / mods.length) : null;
    return { active, open, closingSoon, awaiting, awaitingFrom, avg, modCount: mods.length };
  }, [students, assignments, cohort]);

  const insufficient = (cohort?.modules || []).filter((m) => m.sufficient === false);
  const readyReports = reports.filter((r) => r.status === 'ready');
  const name = user?.firstName || user?.username || '';

  const attention = [];
  stats.closingSoon.forEach((a) => attention.push({
    accent: true,
    title: `${a.template_name || 'Exam'} ${relativeWindow(a.opens_at, a.closes_at).toLowerCase()}`,
    sub: `${Math.max(0, (a.student_count || 0) - (a.completed_count || 0))} of ${a.student_count || 0} students have not submitted.${isProctored(a.mode) ? ' Proctored — no retakes.' : ''}`,
    cta: 'Review', go: () => navigate(kq('assignments')),
  }));
  insufficient.slice(0, 2).forEach((m) => attention.push({
    title: `${m.module} has too little data to score`,
    sub: `${m.attempted || 0} questions attempted across the cohort. ${cohort?.min_questions_for_strength ?? 'More'} are needed before a strength is reported.`,
    cta: 'Open', go: () => navigate(kq('analytics')),
  }));
  if (readyReports.length) attention.push({
    title: `${readyReports.length} PDF report${readyReports.length === 1 ? '' : 's'} finished building`,
    sub: `${readyReports.map((r) => r.label).join(', ')} — ready to download.`,
    cta: 'Download', go: () => navigate(kq('analytics')),
  });

  return (
    <div className="kqc-page">
      <div className="kqc-pagehead">
        <div style={{ minWidth: 0 }}>
          <div className="kqc-kicker" style={{ marginBottom: 8 }}>{fmtLongDay()}</div>
          <h1 className="kqc-h1" style={{ fontSize: 'clamp(26px,3.6vw,44px)' }}>{greeting()}{name ? `, ${name}` : ''}</h1>
        </div>
        <span className="kqc-spacer" />
        <button type="button" className="btn btn-primary" onClick={() => navigate(kq('assignments/new'))} data-tour="ex-assign">Assign an exam</button>
      </div>

      <div className="kqc-stats" data-tour="ex-stats">
        <Stat label="Students" value={students.length} hint={`${stats.active} active · ${students.length - stats.active} disabled`} />
        <Stat label="Open assignments" value={stats.open.length} hint={stats.closingSoon[0] ? `${stats.closingSoon[0].template_name} ${relativeWindow(null, stats.closingSoon[0].closes_at).toLowerCase()}` : 'Nothing closing today'} />
        <Stat label="Awaiting sitting" value={stats.awaiting} accent={stats.awaiting > 0} hint={stats.awaitingFrom ? `${stats.awaitingFrom.template_name} · proctored` : 'No proctored sittings pending'} />
        <Stat label="Cohort average" value={stats.avg == null ? '—' : `${stats.avg}%`} hint={stats.modCount ? `Across ${stats.modCount} module${stats.modCount === 1 ? '' : 's'}` : 'Not enough data yet'} />
      </div>

      <div className="kqc-cols" style={{ gap: 0 }}>
        <div className="is-wide" style={{ paddingRight: 'clamp(0px,2vw,26px)' }}>
          <RuleHead title="Needs your attention" />
          {attention.length === 0 ? (
            <div className="kqc-empty">Nothing needs you right now. Assignments closing within a day, modules short of data and finished reports will appear here.</div>
          ) : (
            <div className="kqc-list">
              {attention.map((a, i) => (
                <button type="button" key={i} className="kqc-list__row" onClick={a.go}>
                  <span className={`kqc-list__bar${a.accent ? ' is-accent' : ''}`} />
                  <span className="kqc-list__body">
                    <span className="kqc-list__title">{a.title}</span>
                    <span className="kqc-list__sub">{a.sub}</span>
                  </span>
                  <span className="kqc-list__cta">{a.cta} &rarr;</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="is-side is-side--rule">
          <RuleHead title="Quick actions" />
          <div className="kqc-quick">
            <button type="button" onClick={() => navigate(kq('students/new'))}><PlusIcon />Add a student</button>
            <button type="button" onClick={() => navigate(kq('students/import'))}><UploadIcon />Import a class</button>
            <button type="button" onClick={() => navigate(kq('templates/build'))}><FileIcon />Build a template</button>
            <button type="button" onClick={() => navigate(kq('analytics'))}><DownloadIcon />Export a report</button>
          </div>
        </div>
      </div>
    </div>
  );
}
